import React from 'react';
import { Trans } from '@lingui/react/macro';

import Card from '@rover/components/src/cards/Card';
import { SERVICE_TYPE_ICONS } from '@rover/components/src/formFields/ServiceTypeFilter/ServiceTypeFilter';
import { SERVICE_TYPE_CHOICES } from '@rover/constants/src/translations/service.constants';
import Boarding from '@rover/icons/svg/boarding.svg';
import { Flex, Heading, Text } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import { GroomingHomepageRebookServiceClick } from '@rover/react-lib/src/pages/grooming/analyticsEvents';
import NewServiceBadge from '@rover/react-lib/src/pages/grooming/components/NewServiceBadge';
import { GROOMING_SEARCH_URL } from '@rover/react-lib/src/pages/grooming/constants';
import useInGroomingPilot from '@rover/react-lib/src/pages/grooming/hooks/useInGroomingPilot';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

import TrainingUrls from '../../../../training/constants/urls';
import ServiceTypeDescription from '../../ServiceTypeDescription';

export type Props = {
  services?: ServiceTypeDescription[];
};

const RebookingSearch = ({ services = [] }: Props): JSX.Element => {
  const { borderWidth, colors, background, textColor } = useTheme();
  const { shouldShowGroomingEntrypoints } = useInGroomingPilot();

  const visibleServices = services.filter(
    (service) => shouldShowGroomingEntrypoints || service.slug !== SERVICE_TYPE_CHOICES.training
  );

  return (
    <Card display="flex" flexDirection="column" flex="1 1 0">
      <Heading size="300">
        <Trans>Search for a service</Trans>
      </Heading>
      <Flex flexGrow="1" flexDirection="column" gap="3x" justifyContent="flex-start">
        {visibleServices.map((service) => {
          const IconService = SERVICE_TYPE_ICONS[service.slug] || Boarding;
          const name = ServiceTypeDescription.NAMES()[service.slug];
          const isGrooming = service.slug === SERVICE_TYPE_CHOICES.grooming;
          const isTraining = service.slug === SERVICE_TYPE_CHOICES.training;
          const shouldShowNewBadge = isGrooming;

          let searchUrl = service.webAction;
          if (isGrooming) {
            searchUrl = GROOMING_SEARCH_URL;
          } else if (isTraining) {
            searchUrl = TrainingUrls.SEARCH;
          }

          return (
            name && (
              <Flex
                key={service.slug}
                borderRadius="5px"
                border={`${borderWidth.primary} solid ${colors.border.primary}`}
                alignItems="center"
                paddingY="2x"
                paddingX="4x"
                flexShrink="1"
                minHeight="56px"
                minWidth="229px"
                webAs="a"
                webAsProps={{
                  href: searchUrl,
                  onClick: isGrooming
                    ? () => emitAnalyticsEvent(new GroomingHomepageRebookServiceClick())
                    : undefined,
                }}
                sx={{
                  '&:hover': {
                    backgroundColor: background.secondary,
                  },
                  '&:focus, &:visited, &:hover': {
                    textDecoration: 'none !important',
                  },
                }}
              >
                <Flex
                  width="20px"
                  height="20px"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink="0"
                >
                  <IconService width="20px" fill={textColor.secondary} />
                </Flex>

                <Text size="100" ml="4x" fontWeight="regular" textColor="secondary">
                  {name}
                </Text>

                {shouldShowNewBadge && (
                  <Flex ml="2x">
                    <NewServiceBadge />
                  </Flex>
                )}
              </Flex>
            )
          );
        })}
      </Flex>
    </Card>
  );
};

export default RebookingSearch;
